import React from 'react';
import classNames from 'classnames';
import { Asset, CachedIcon } from '@clearcover/atlas';

import { HRule, NumericFont } from 'Shared/components';
import { isInstallmentPlan } from 'QuoteApp/utils/paymentPlan';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext } from 'QuoteApp/services/providers';

import { PaymentScheduleModal } from '../PaymentScheduleModal';
import {
  DownpaymentBreakdownSection,
  PremiumPricingSection,
  PricingSection,
  SummarySection,
} from './SidebarSections';
import styles from './styles.module.css';

type StandardPaymentSidebarProps = {
  paymentPlan: PaymentPlanType;
  excludedDrivers: number;
  includedDrivers: number;
};

export const StandardPaymentSidebar: React.FC<StandardPaymentSidebarProps> = ({
  paymentPlan,
  excludedDrivers,
  includedDrivers,
}) => {
  const { config, details } = useAppContext();

  const isAccessEnabled = !!config?.accessEnabled;

  const installmentFeeMessage = paymentPlan?.totalAmountBreakdown
    ? null
    : paymentPlan?.messages.find(m => m.type === 'fee')?.description;

  return (
    <>
      <div className={classNames(styles.section, styles.dueToday)}>
        <h2 className={styles.sectionHeader}>
          Amount due today{installmentFeeMessage && '*'}
        </h2>
        <div className={styles.paymentDue}>
          <NumericFont value={paymentPlan.downPaymentAmount} />
        </div>
        {installmentFeeMessage && (
          <div className={styles.feeDisclaimer}>
            <span>*</span>
            <span>{installmentFeeMessage}</span>
          </div>
        )}
        <HRule size="sm" />
        {isInstallmentPlan(paymentPlan) && (
          <>
            <PaymentScheduleModal currentPlan={paymentPlan} />
            <HRule size="sm" />
          </>
        )}
      </div>

      {paymentPlan.downPaymentBreakdown && (
        <>
          <DownpaymentBreakdownSection paymentPlan={paymentPlan} />
          <HRule size="sm" />
        </>
      )}

      <SummarySection
        productName="Auto"
        effectiveDate={details.effectiveDate.value}
        excludedDrivers={excludedDrivers}
        numberOfDrivers={includedDrivers}
        numberOfVehicles={Object.keys(details.vehicles).length}
      />

      {paymentPlan.totalAmountBreakdown ? (
        <>
          <HRule size="sm" />
          <PricingSection
            paymentPlan={paymentPlan}
            isAccessEnabled={isAccessEnabled}
          />
        </>
      ) : (
        <>
          <HRule size="sm" />
          <PremiumPricingSection
            totalAmount={paymentPlan.totalAmount}
            totalPremiumAmount={paymentPlan.totalPremiumAmount}
            totalTaxAmount={paymentPlan.totalTaxAmount}
            totalTaxAndFeeAmount={paymentPlan.totalTaxAndFeeAmount}
          />
        </>
      )}

      <div className={classNames(styles.section, styles.premiumAutoRenew)}>
        <Asset
          className={styles.premiumAutoRenewIcon}
          icon={CachedIcon}
          description=""
          height={16}
          width={16}
        />
        Policy will auto-renew if eligible
      </div>
    </>
  );
};
